import { PivotTableApiResponse, ServiceAttributeSummaryData } from '@/models/PulseData';
import userService from './userService';
import logger from '../utils/logger';
import { TimerIcon } from 'lucide-react';

// Internal helper function to fetch and validate pivot data
async function _fetchAndValidatePivotData(
  apiKey: string | null,
  endpoint: string,
  queryParams: URLSearchParams,
  purpose: string
): Promise<PivotTableApiResponse> {
  try {
    const data = await userService.authenticatedFetch<PivotTableApiResponse>(
      purpose,
      apiKey,
      'GET',
      endpoint,
      queryParams,
    );
    // Unified validation
    if (!data || !data.pivotTable) {
      const errorMessage = `${purpose} data not found or malformed in API response (missing pivotTable property or data is null)`;
      logger.error(errorMessage, { responseData: data, url: endpoint, queryParams: queryParams.toString() });
      throw new Error(errorMessage);
    }
    return data;
  } catch (error: any) {
    throw error; // Re-throw to be handled by the caller
  }
}

// Shared parameters interface for pivot data fetching functions
interface FetchPivotApiParams {
  apiKey: string | null;
  customer: string;
  startDate: Date;
  endDate: Date;
  servicesList: string[]; // had one or more services for services-pivot; only one service when requesting service-attribute pivot
  attribute: string | null;     // Optional, only for service attribute pivot.
  timeIncrement?: number;
}
/**
 * Fetches the pivot table data for error counts from the backend API.
 * @param params - The parameters for the API call (customer, startDate, endDate).
 * @returns A promise that resolves to the PivotTableApiResponse.
 * @throws Will throw an error if the network response is not ok or if data is malformed.
 */
export const fetchPulsePivotTableData = async (
  params: FetchPivotApiParams
): Promise<PivotTableApiResponse> => {
  const { apiKey, customer, startDate, endDate, servicesList, timeIncrement } = params;
  const endpoint = '/api/pulse/pivot/services';
  const purpose = 'pivot table data';

  if (servicesList.length === 0) {
    throw new Error("Parameter 'servicesList' should have at least one entry to fetch pivot data.");
  }

  const queryParamsObj: Record<string, string> = {
    customer,
    view: servicesList.join(','),
    start: startDate.toISOString(),
    end: endDate.toISOString(),
    ti: String(timeIncrement)
  };

  console.log( queryParamsObj);

  const queryParams = new URLSearchParams(queryParamsObj);

  return _fetchAndValidatePivotData(apiKey, endpoint, queryParams, purpose);
};


/**
 * Fetches pivot table data for a specific service and attribute.
 * @param params - The parameters for the API call.
 * @returns A promise that resolves to the ServiceAttributePivotApiResponse.
 * @throws Will throw an error if the network response is not ok or if data is malformed.
 */
export const fetchPulseServiceAttributePivotData = async (
  params: FetchPivotApiParams
): Promise<PivotTableApiResponse> => {

  const { apiKey, customer, attribute, startDate, endDate, servicesList } = params;
  let attributeName = attribute || ''

  if (servicesList.length !== 1) {
    throw new Error("Parameter 'servicesList' is required to have just one service forfetching service attribute pivot data.");
  }
  // if (!attribute) {
  //   throw new Error("Parameter 'attribute' is required for fetching service attribute pivot data.");
  // }

  const endpoint = '/api/pulse/pivot/service_attribute';
  const purpose = 'Service attribute pivot';

  const queryParamsObj: Record<string, string> = {
    customer,
    service: servicesList[0], // ToDo: eliminate this from the API call requirement
    view: servicesList.join(','),
    attribute: attributeName,
    start: startDate.toISOString(),
    end: endDate.toISOString(),
  };
  const queryParams = new URLSearchParams(queryParamsObj);

  return _fetchAndValidatePivotData(apiKey, endpoint, queryParams, purpose);
};

interface FetchServiceAttributeParams {
    apiKey: string | null;
    customer: string;
    service: string;
    attribute: string;
    startDate: Date;
    endDate: Date;
    servicesGroup?: string; // optional for scoping purposes
}

/**
 * Fetches per-service attribute summary data from the backend API.
 * @param params - The parameters for the API call.
 * @returns A promise that resolves to the ServiceAttributeSummaryData.
 * @throws Will throw an error if the network response is not ok or if data is malformed.
 */
export const fetchPulseServiceAttributeData = async (
    params: FetchServiceAttributeParams
  ): Promise<ServiceAttributeSummaryData> => {
    
  const { apiKey, customer, service, attribute, startDate, endDate } = params;
  const endpoint = '/api/pulse/counts';
  const purpose = 'Service attribute counts';

  const queryParamsObj: Record<string, string> = {
    customer,
    services:service,
    attribute,
    start: startDate.toISOString(),
    end: endDate.toISOString(),
    // servicesGroup, // pass it down if the fetchPulseServiceAttributeDAta needs it
  };
  
  const queryParams = new URLSearchParams(queryParamsObj);  
  try {
    const data: ServiceAttributeSummaryData = 
      await userService.authenticatedFetch<ServiceAttributeSummaryData>(
      purpose,
      apiKey,
      'GET',
      endpoint,
      queryParams,
      );

    // Add any specific validation for ServiceAttributeSummaryData if needed
    if (!data || !data.services || !data.attribute || !data.counts) {
      logger.error('Service attribute data not found or malformed in API response', { responseData: data, url: endpoint });
      throw new Error('Service attribute data not found or malformed in API response');
    }
    return data;
  } catch (error: any) {
    // Error is already logged by authenticatedFetch.
    // This catch block ensures the error propagates up to the caller.
    throw error; 
  }
};
  

interface FetchServiceAttributesParams {
  apiKey: string | null;
  customer: string;
  service: string;
  attributes: string[]; // Array of attribute names to fetch
  startDate: Date;
  endDate: Date;
  servicesGroup?: string; // optional for scoping purposes
}

interface FetchServiceAttributesResult {
  attribute: string;
  data: ServiceAttributeSummaryData | null;
  error: string | null;
}

/**
 * Fetches per-service attribute summary data for multiple attributes concurrently.
 * @param params - The parameters for the API calls, including a list of attributes.
 * @returns A promise that resolves to an array of results, one for each attribute.
 */
export const fetchPulseServiceAttributesData = async (
  params: FetchServiceAttributesParams
): Promise<FetchServiceAttributesResult[]> => {

  const { apiKey, customer, service, attributes, startDate, endDate } = params;

  const attributePromises = attributes.map(async (attribute) => {
    try {
      const data = await fetchPulseServiceAttributeData({
        apiKey,
        customer,
        service,
        attribute,
        startDate,
        endDate,
        
      });
      return { attribute, data, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `Unknown error fetching ${attribute}.`;
      return { attribute, data: null, error: errorMessage };
    }
  });

  return Promise.all(attributePromises); // Use Promise.all since errors are handled within the map
};

interface FetchServicesGroupAttributesParams {
  apiKey: string | null;
  customer: string,
  servicesList: string[],
  attribute: string,
  startDate: Date,
  endDate: Date,
}

/**
 * Fetches pivot table data for a specific service and attribute.
 * @param params - The parameters for the API call.
 * @returns A promise that resolves to the ServiceAttributePivotApiResponse.
 * @throws Will throw an error if the network response is not ok or if data is malformed.
 */
export const fetchServicesGroupAttributeSummary = async (
  params: FetchServicesGroupAttributesParams,
): Promise<ServiceAttributeSummaryData> => {

  const { apiKey, customer, servicesList, attribute, startDate, endDate } = params;
  const endpoint = '/api/pulse/counts'; // with a list of services given as parameter
  const purpose = 'Service Group attribute counts';

  if (!customer || !servicesList || !attribute || !startDate || !endDate) {
    throw new Error(
      'Customer ID, servicesGroup, attribute, start date, and end date are required for fetching services group attribute summary.',
    );
  }

  const queryParamsObj: Record<string, string> = {
    customer,
    services: servicesList.join(','),
    attribute,
    start: startDate.toISOString(),
    end: endDate.toISOString(),
  };
  const queryParams = new URLSearchParams(queryParamsObj);  

  // curl "http://localhost:3010/api/pulse/counts?customer=customer1&ervices=test_service&attribute=http_path&start=2025-04-22&end=2025-05-10&"
  try {
    const data: ServiceAttributeSummaryData = 
      await userService.authenticatedFetch<ServiceAttributeSummaryData>(
        purpose,
        apiKey,
        'GET',
        endpoint,
        queryParams,
      );

    // Add any specific validation for ServiceAttributeSummaryData if needed
    // ToDo: change data.service to be data.servicesGroup in the future
    if (!data || !data.services || !data.attribute || !data.counts) {
      logger.error('Service attribute data not found or malformed in API response', { responseData: data, url: endpoint });
      throw new Error('Service attribute data not found or malformed in API response');
    }
    return data;
  } catch (error: any) {
    // Error is already logged by authenticatedFetch.
    // This catch block ensures the error propagates up to the caller.
    throw error; 
  }  
};